import { useState } from 'react';
import { formatDateForUI, getServiceTypeString, capitalize } from '../../appointment/appointmentFunctions';
import AppointmentStatusBadge from './AppointmentStatusBadge';
import AppointmentActions from './AppointmentActions';
import { useSelector } from 'react-redux';
import { getPatientVisiblePractices } from '../utils/patientPracticeUtils';

const PatientAppointmentRow = ({ 
    appointment, 
    serviceTypes, 
    onDetailsClick, 
    onDeleteClick, 
    onViewPracticesClick, 
    ChatButton,
    roomsNotificationCount,
    onViewChat,
    loggedInUser,
    dispatch
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const selectedAppointment = useSelector(state => state.appointmentDetails?.selectedAppointment);
    
    // Check if this appointment is selected
    const isSelected = selectedAppointment && selectedAppointment._id === appointment._id;
    
    const handleCellClick = () => {
        const practiceCount = getPatientVisiblePractices(appointment).length;
        
        if (practiceCount > 1) {
            // Toggle accordion for multiple clinics
            setIsExpanded(!isExpanded);
        } else {
            // Show details for single or no clinics
            onDetailsClick(appointment);
        }
    };

    const handleExpandClick = (e) => {
        e.stopPropagation();
        setIsExpanded(!isExpanded);
    };


    const visiblePractices = getPatientVisiblePractices(appointment);
    const practiceCount = visiblePractices.length;

    return (
        <div className="border-b border-gray-200">
            {/* Main appointment row */}
            <div className={`grid grid-cols-8 gap-4 p-4 border-b border-gray-100 transition-colors ${
                isSelected ? 'bg-blue-50 border-l-4 border-l-blue-400' : 'hover:bg-gray-50'
            }`} style={{gridTemplateColumns: '40px 1fr 1fr 1.2fr 1fr 1.5fr 1fr 1fr'}}>
                {/* Expand/Collapse Arrow */}
                <div className="font-inter font-normal text-[16px] leading-[20px] tracking-normal text-[#2C2C2C] flex items-center justify-center">
                    <div 
                        onClick={handleExpandClick}
                        className={`flex items-center justify-center w-6 h-6 rounded transition-all ${
                            practiceCount <= 1 
                                ? 'text-gray-300 cursor-not-allowed' 
                                : 'text-gray-600 hover:text-blue-600 cursor-pointer hover:bg-gray-100'
                        }`}
                        title={practiceCount <= 1 ? 'No clinics to expand' : (isExpanded ? 'Collapse clinics' : 'Expand clinics')}
                    >
                        <svg 
                            className={`w-4 h-4 transition-transform duration-200 ${
                                practiceCount <= 1 ? '' : (isExpanded ? 'rotate-90' : '')
                            }`}
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </div>
                </div>
                
                {/* Speciality */}
                <div 
                    onClick={handleCellClick}
                    className="font-inter font-normal text-[16px] leading-[20px] tracking-normal text-[#2C2C2C] cursor-pointer hover:text-blue-600 transition-colors flex items-center min-h-[32px]"
                >
                    {getServiceTypeString(appointment.service_type, serviceTypes)}
                </div>
                
                {/* Appointment For */}
                <div 
                    onClick={handleCellClick}
                    className="font-inter font-normal text-[16px] leading-[20px] tracking-normal text-[#2C2C2C] cursor-pointer hover:text-blue-600 transition-colors flex items-center min-h-[32px]"
                >
                    {capitalize(appointment.appointment_for)}
                </div>
                
                {/* Requested On */}
                <div 
                    className="font-inter font-normal text-[16px] leading-[20px] tracking-normal text-[#2C2C2C] flex items-center min-h-[32px]"
                >
                    {formatDateForUI(appointment.create_date_time)}
                </div>
                
                {/* Urgency */}
                <div 
                    className="font-inter font-normal text-[16px] leading-[20px] tracking-normal text-[#2C2C2C] flex items-center min-h-[32px]"
                >
                    {appointment.urgency_type}
                </div>
                
                {/* Clinic - Show clinic name or count */}
                <div className="font-inter font-normal text-[16px] leading-[20px] tracking-normal text-[#2C2C2C] flex items-center min-h-[32px]">
                    {(() => {
                        if (practiceCount === 0) {
                            return <span className="text-gray-400">None</span>;
                        } else if (practiceCount === 1) {
                            if (visiblePractices[0]?.location_name) {
                                return (
                                    <span className="text-gray-600">
                                        {visiblePractices[0].location_name}
                                    </span>
                                );
                            } else {
                                return (
                                    <span className="text-blue-600">
                                        1 Clinic
                                    </span>
                                );
                            }
                        } else {
                            return (
                                <div 
                                    onClick={handleExpandClick}
                                    className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded transition-colors"
                                    title={isExpanded ? "Collapse clinics" : "Expand clinics"}
                                >
                                    <span className="text-gray-700 font-bold">
                                        Multiple Clinics
                                    </span>
                                    <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center">
                                        <span className="text-xs font-medium text-gray-700">
                                            {practiceCount}
                                        </span>
                                    </div>
                                </div>
                            );
                        }
                    })()}
                </div>
                
                {/* Status */}
                <div className="font-inter font-normal text-[16px] leading-[20px] tracking-normal text-[#2C2C2C] flex items-center min-h-[32px]">
                    <AppointmentStatusBadge status={appointment.patient_appointment_status} />
                </div>
                
                {/* Actions - Desktop */}
                <div className="flex font-inter font-normal text-[16px] leading-[20px] tracking-normal text-[#2C2C2C] flex items-center min-h-[32px]">
                    <AppointmentActions
                        appointment={appointment}
                        onDeleteClick={onDeleteClick}
                        onViewPracticesClick={onViewPracticesClick}
                        onViewDetailsClick={onDetailsClick}
                        onViewChat={onViewChat}
                        isMobile={false}
                        ChatButton={ChatButton}
                        roomsNotificationCount={roomsNotificationCount}
                    />
                </div>
                
                {/* Actions - Mobile */}
                <div className="flex lg:hidden font-inter font-normal text-[16px] leading-[20px] tracking-normal text-[#2C2C2C] flex items-center min-h-[32px]">
                    <AppointmentActions
                        appointment={appointment}
                        onDeleteClick={onDeleteClick}
                        onViewPracticesClick={onViewPracticesClick}
                        onViewDetailsClick={onDetailsClick}
                        onViewChat={onViewChat}
                        isMobile={true}
                        ChatButton={ChatButton}
                        roomsNotificationCount={roomsNotificationCount}
                    />
                </div>
            </div>

            {/* Practice rows - hidden by default, shown when expanded */}
            {visiblePractices.map((practice, index) => {
                if (practiceCount <= 1) return null; // Only show for multiple clinics
                
                return (
                    <div key={index} className={`grid grid-cols-8 gap-4 p-4 transition-all duration-300 ${
                        isExpanded ? 'block' : 'hidden'
                    } ${
                        index < visiblePractices.length - 1 ? 'border-b border-gray-100' : ''
                    }`} style={{gridTemplateColumns: '40px 1fr 1fr 1.2fr 1fr 1.5fr 1fr 1fr'}}>
                        {/* Empty column for arrow */}
                        <div className="hidden lg:block"></div>
                        
                        {/* Empty columns for next 4 columns */}
                        <div className="hidden lg:block"></div>
                        <div className="hidden lg:block"></div>
                        <div className="hidden lg:block"></div>
                        <div className="hidden lg:block"></div>
                        
                        {/* Clinic - Location name */}
                        <div className="hidden lg:block font-inter font-normal text-[16px] leading-[20px] tracking-normal text-[#2C2C2C] flex items-center min-h-[32px]">
                            {practice.location_name}
                        </div>
                        
                        {/* Status - Same badge style as main row */}
                        <div className="hidden lg:block font-inter font-normal text-[16px] leading-[20px] tracking-normal text-[#2C2C2C] flex items-center min-h-[32px]">
                            <AppointmentStatusBadge status={practice.practice_appointment_status} />
                        </div>
                        
                        {/* Actions - Chat button */}
                        <div className="hidden lg:block font-inter font-normal text-[16px] leading-[20px] tracking-normal text-[#2C2C2C] flex items-center min-h-[32px]">
                            <div className="flex items-center gap-2">
                                {(() => {
                                    return (
                                        <button
                                            type="button"
                                            onClick={() => onViewChat(appointment, practice)}
                                            className="inline-flex items-center justify-center w-12 h-12 rounded-md hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
                                            title="Chat"
                                        >
                                            <img src={ChatButton} className='w-8 h-8' alt='Chat' />
                                        </button>
                                    );
                                })()}
                            </div>
                        </div>
                        
                        {/* Mobile view - Full width */}
                        <div className="flex lg:hidden col-span-2 md:col-span-3">
                            <div className="w-full">
                                <div className="flex justify-between items-center mb-2">
                                    <span className="font-medium text-gray-900">{practice.location_name}</span>
                                    <AppointmentStatusBadge status={practice.practice_appointment_status} />
                                </div>
                                <div className="flex justify-end">
                                    {(() => {
                                        return (
                                            <button
                                                type="button"
                                                onClick={() => onViewChat(appointment, practice)}
                                                className="inline-flex items-center justify-center w-12 h-12 rounded-md hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
                                                title="Chat"
                                            >
                                                <img src={ChatButton} className='w-8 h-8' alt='Chat' />
                                            </button>
                                        );
                                    })()}
                                </div>
                            </div>
                        </div>
                        
                        {/* Mobile main appointment row */}
                        <div className="flex lg:hidden col-span-2 md:col-span-3">
                            <div className="w-full mb-4 p-4 border border-gray-200 rounded-lg">
                                <div className="space-y-3">
                                    {/* Speciality */}
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm font-medium text-gray-600">Speciality:</span>
                                        <span className="text-sm font-medium text-gray-900">
                                            {(() => {
                                                const serviceType = serviceTypes?.find(st => st.service_id === appointment.service_type);
                                                return serviceType?.service_name || 'Unknown Service';
                                            })()}
                                        </span>
                                    </div>
                                    
                                    {/* Appointment For */}
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm font-medium text-gray-600">Appointment For:</span>
                                        <span className="text-sm font-medium text-gray-900">{appointment.appointment_for}</span>
                                    </div>
                                    
                                    {/* Requested On */}
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm font-medium text-gray-600">Requested On:</span>
                                        <span className="text-sm font-medium text-gray-900">
                                            {(() => {
                                                const date = new Date(appointment.create_date_time);
                                                return date.toLocaleDateString();
                                            })()}
                                        </span>
                                    </div>
                                    
                                    {/* Urgency */}
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm font-medium text-gray-600">Urgency:</span>
                                        <span className="text-sm font-medium text-gray-900">{appointment.urgency_type}</span>
                                    </div>
                                    
                                    {/* Status */}
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm font-medium text-gray-600">Status:</span>
                                        <AppointmentStatusBadge status={appointment.patient_appointment_status} />
                                    </div>
                                    
                                    {/* Actions */}
                                    <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                                        <span className="text-sm font-medium text-gray-600">Actions:</span>
                                        <div className="flex gap-2">
                                            <AppointmentActions
                                                appointment={appointment}
                                                onDeleteClick={onDeleteClick}
                                                onViewPracticesClick={onViewPracticesClick}
                                                onViewDetailsClick={onDetailsClick}
                                                onViewChat={onViewChat}
                                                isMobile={true}
                                                ChatButton={ChatButton}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export { PatientAppointmentRow };
export default PatientAppointmentRow;
